import React, { Component, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Alert } from "reactstrap";
import "./loader.css";
import ExceptionFormatter from "shared/formatters/ExceptionFormatter";

function DEFAULT_ERROR_AND_LOADING_WRAPPER(components: ReactNode): ReactNode {
  return components;
}

export type LoaderChildren<P> = (resultProps: P) => ReactNode;

export interface ILoaderProps<P> {
  promise?: Promise<P>;
  children?: LoaderChildren<P>;
  statusWrapper?: (components: ReactNode) => ReactNode;
}

interface ILoaderState<P> {
  error?: Error;
  loading: boolean;
  result?: P;
}

export default class Loader<P> extends Component<
  ILoaderProps<P>,
  ILoaderState<P>
> {
  private isUnmounted: boolean = false;

  public state = {
    loading: true,
    result: undefined,
    error: undefined,
  };

  public componentDidMount(): void {
    this.onLoad();
  }

  public componentDidUpdate(prevProps: ILoaderProps<P>): void {
    if (this.props.promise !== prevProps.promise) {
      this.onLoad();
    }
  }

  public componentWillUnmount(): void {
    this.isUnmounted = true;
  }

  private async onLoad(): Promise<void> {
    const { promise } = this.props;

    if (promise === undefined) {
      return;
    }

    this.setState({
      loading: true,
      error: undefined,
    });

    try {
      const result: P = await promise;
      if (!this.isUnmounted) {
        this.setState({
          result,
        });
      }
    } catch (ex) {
      if (!this.isUnmounted) {
        this.setState({
          error: ex as Error,
        });
      }
    } finally {
      if (!this.isUnmounted) {
        this.setState({
          loading: false,
        });
      }
    }
  }

  public render() {
    const { statusWrapper = DEFAULT_ERROR_AND_LOADING_WRAPPER } = this.props;
    const { error } = this.state;
    if (error !== undefined) {
      return statusWrapper(
        <Alert color="danger">
          <span className="fas fa-exclamation-circle" />{" "}
          <ExceptionFormatter exception={error!} />
        </Alert>,
      );
    }

    const { loading } = this.state;
    if (loading) {
      return statusWrapper(
        <p className="text-center">
          <span className="fas fa-spinner text-success" />{" "}
          <FormattedMessage id="ui.loading" />
        </p>,
      );
    }

    const { children } = this.props;
    if (children) {
      const { result } = this.state;
      return children(result!);
    } else {
      return null;
    }
  }
}
